import React, { useEffect, useState } from 'react';
import { Box, TableBody } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import {
  MainContainer,
  HeaderContainer,
  PageTitle,
  CreateButton,
  StyledTableContainer,
  StyledTable,
  StyledTableHead,
  StyledTableRow,
  StyledTableCell,
  ActionButton,
} from './styles';
import { useBreadcrumb } from '../../../../context/BreadcrumbContext';
import { useSidebar } from '../../../../context/SidebarContext';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../../../store';
import { fetchOrgRoles, resetAccessControlActionState } from '../../../../modules/accessControl';
import { selectOrgRoles, selectOrgRolesLoading } from '../../../../modules/accessControl/selectors';
import RoleModal from './components/RoleModal';
import AlertPopup from '../../../../components/common/AlertPopup';

const SystemRoles: React.FC = () => {
  const { setActivePage } = useSidebar();
  const { setPath, setIsShow } = useBreadcrumb();
  const dispatch = useDispatch<AppDispatch>();

  const roles = useSelector(selectOrgRoles);
  const isLoading = useSelector(selectOrgRolesLoading);

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRole, setSelectedRole] = useState<any>(null);

  // Alert state
  const [alert, setAlert] = useState<{ message: string, severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setActivePage('org-system-roles');
    setPath([
      { title: 'Access Control', path: '/access-control/roles' },
      { title: 'System Roles', path: '/access-control/roles' },
    ]);
    setIsShow(true);
    dispatch(fetchOrgRoles());

    return () => {
      dispatch(resetAccessControlActionState());
    };
  }, [setActivePage, setPath, setIsShow, dispatch]);

  const handleCreateClick = () => {
    setSelectedRole(null);
    setIsModalOpen(true);
  };

  const handleEditClick = (role: any) => {
    setSelectedRole(role);
    setIsModalOpen(true);
  };

  return (
    <Box>
      {alert && <AlertPopup message={alert.message} severity={alert.severity} open={!!alert} onClose={() => setAlert(null)} />}
      <MainContainer>
        <HeaderContainer>
          <PageTitle variant="h6">Organization System Roles</PageTitle>
          <CreateButton variant="contained" disableElevation onClick={handleCreateClick}>
            Create Role
          </CreateButton>
        </HeaderContainer>

        <StyledTableContainer>
          <StyledTable>
            <StyledTableHead>
              <StyledTableRow>
                <StyledTableCell>Id</StyledTableCell>
                <StyledTableCell>Role Name</StyledTableCell>
                <StyledTableCell>Description</StyledTableCell>
                <StyledTableCell>Action</StyledTableCell>
              </StyledTableRow>
            </StyledTableHead>
            <TableBody>
              {roles && roles.length > 0 ? (
                roles.map((role: any) => (
                  <StyledTableRow key={role.role_id}>
                    <StyledTableCell>{role.role_id}</StyledTableCell>
                    <StyledTableCell>{role.role_name}</StyledTableCell>
                    <StyledTableCell>{role.role_description}</StyledTableCell>
                    <StyledTableCell>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <ActionButton onClick={() => handleEditClick(role)}>
                          <EditIcon fontSize="small" />
                        </ActionButton>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              ) : (
                <StyledTableRow>
                  <StyledTableCell colSpan={4} align="center">
                    {isLoading ? 'Loading roles...' : 'No roles found.'}
                  </StyledTableCell>
                </StyledTableRow>
              )}
            </TableBody>
          </StyledTable>
        </StyledTableContainer>
      </MainContainer>

      <RoleModal
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          dispatch(fetchOrgRoles());
        }}
        role={selectedRole}
      />
    </Box>
  );
};

export default SystemRoles;
